import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Receipt, Search, AlertCircle, RefreshCw, FileText, Pill, Stethoscope, ChevronRight, CreditCard, Eye,
  Package, Printer, Banknote, Wallet, Syringe, AlertTriangle,
} from 'lucide-react';
import { invoiceAPI, clinicalSettingsAPI, brandingAPI } from '../services/api';
import { useToast } from './UIProvider';
import DatePicker from './DatePicker';
import SortHeader, { useSort, sortRows } from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';
import { formatClinicDate, formatClinicDateTime, formatClinicTime, clinicToday, toClinicDate } from '../utils/clinicTime';
import { scrollContentToTop } from '../utils/scroll';
import { medicineTypeIcon } from '../constants/medicineTypes';
import { OpdReceiptsView } from './TokenManagement';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';
import { PAYMENT_MODE_LABEL } from '../constants/payments';
import { formatRs, formatSigned, toPaisa } from '../utils/money';
import BillingCounter, { counterHash, parseCounterHash } from './invoices/BillingCounter';
import PrintableInvoice from './invoices/PrintableInvoice';
import { receiptDoctorName, invoiceTotals } from './invoices/InvoiceReceipt';
import InvoiceReceiptModal from './invoices/InvoiceReceiptModal';
import CashDrawersView from './cashDrawer/CashDrawersView';

// A later visit's bill for an injection or drip course: the patient came back for the next dose.
// It names the prescription and the visit the course was prescribed on, and the patient takes no
// token for it, so a day's visit count leaves it out. That is why a day can show more invoices than
// visits (26 Oct 2026 on prod: 41 invoices, 39 visits, two of them doses for courses begun on the
// 23rd and the 24th).
export const isCourseDose = (inv) => !!inv?.treatmentCourseId;

/** The dose a course bill carries (a list row says it; a whole bill's lines are marked with it), or null. */
export const doseNumberOf = (inv) => {
  if (inv?.doseNumber) return inv.doseNumber;
  const n = Math.max(0, ...(inv?.items || []).map((it) => Number(it.courseDoseNumber) || 0));
  return n > 0 ? n : null;
};

const courseDoseTitle = (inv) => {
  const dose = doseNumberOf(inv);
  const day = inv?.visit?.visitDate ? formatClinicDate(String(inv.visit.visitDate).slice(0, 10)) : '';
  return `Back for ${dose ? `dose ${dose}` : 'the next dose'} of an injection course prescribed${day ? ` on ${day}` : ''}`
    + `${inv?.prescription?.prescriptionNumber ? ` (${inv.prescription.prescriptionNumber})` : ''}.`
    + ' No token is taken for it, so the day\'s visit count leaves it out.';
};

const CourseDoseChip = ({ invoice, className = '' }) => {
  const dose = doseNumberOf(invoice);
  return (
    <span
      title={courseDoseTitle(invoice)}
      className={`inline-flex w-fit items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 cursor-help whitespace-nowrap ${className}`}
    >
      <Syringe className="h-3 w-3" aria-hidden="true" /> Injection dose{dose ? ` ${dose}` : ''}
    </span>
  );
};

// Date filter chips for the invoice list (mirrors the All Patients / Prescription History lists).
const INVOICE_DATE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom' },
];
// [start, end) Date range for a list filter — used to filter client-side by the completed date.
const invoiceListRange = (filter, customStart, customEnd) => {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now);
  const tomorrow = new Date(today.getTime() + 86400000);
  switch (filter) {
    case 'today': return [today, tomorrow];
    case 'yesterday': return [new Date(today.getTime() - 86400000), today];
    case 'week': {
      const dow = today.getDay();
      const monday = new Date(today.getTime() - (dow === 0 ? 6 : dow - 1) * 86400000);
      return [monday, tomorrow];
    }
    case 'month': return [new Date(now.getFullYear(), now.getMonth(), 1), tomorrow];
    case 'custom': return [
      customStart ? new Date(customStart + 'T00:00:00') : null,
      customEnd ? new Date(new Date(customEnd + 'T00:00:00').getTime() + 86400000) : null,
    ];
    default: return [null, null];
  }
};

const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** The same range as the server takes it: the first and the last day, both included, either open. */
const invoiceListDays = (filter, customStart, customEnd) => {
  const [start, end] = invoiceListRange(filter, customStart, customEnd);
  return [
    start ? isoDay(start) : null,
    end ? isoDay(new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1)) : null,
  ];
};

const formatCurrency = (amt) => formatRs(amt || 0);

const INVOICE_STATUS_CONFIG = {
  DRAFT:     { label: 'Draft',     color: 'bg-amber-100 text-amber-800',   dot: 'bg-amber-500' },
  FINALIZED: { label: 'Finalized', color: 'bg-blue-100 text-blue-800',     dot: 'bg-blue-500' },
  PAID:      { label: 'Paid',      color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600',     dot: 'bg-gray-400' },
};

const ITEM_TYPE_ICONS = {
  MEDICINE: Pill,
  MEDICAL_SERVICE: Stethoscope,
  EXTRA_SERVICE: Package,
};

/**
 * The icon for one invoice line. A medicine is drawn as what it actually is, taking the form from
 * the prescription line the item was billed from, so a syrup and a capsule no longer share the
 * generic pill. Everything else is known by its item type alone.
 */
const itemIcon = (item) =>
  item.itemType === 'MEDICINE'
    ? medicineTypeIcon(item.prescriptionMedicine?.medicineType, Pill)
    : ITEM_TYPE_ICONS[item.itemType] || Package;

// ============================================================================
// MAIN COMPONENT
// ============================================================================
// Three tabs. The Counter is where a bill is made, paid and printed in one place; All invoices is
// the history, where a paid or cancelled bill opens read-only and a bill still in progress opens
// back on the counter; Token Receipts are the consultation fees taken at token issue.
const InvoiceManagement = ({ hasPermission }) => {
  const showToast = useToast();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('INVOICE', action) : true),
    [hasPermission],
  );
  // Every tab is a permission of its own, so reception can be given the counter without every bill
  // in the hospital. The page opens on the first tab this user actually has.
  const mayViewDrawers = typeof hasPermission === 'function' && hasPermission('CASH_DRAWERS', 'VIEW');
  const firstTab = useCallback(() => {
    if (can('VIEW_COUNTER_TAB')) return 'counter';
    if (can('VIEW_ALL_INVOICES_TAB')) return 'list';
    if (can('VIEW_TOKEN_RECEIPTS_TAB')) return 'tokenReceipts';
    if (mayViewDrawers) return 'drawers';
    return 'none';
  }, [can, mayViewDrawers]);

  const [view, setView] = useState(firstTab); // 'counter' | 'list' | 'detail' | 'tokenReceipts' | 'drawers' | 'none'
  // Switching the primary view swaps the whole page content inside the app <main>; reset scroll
  // so each view starts at the top.
  useEffect(() => { scrollContentToTop(); }, [view]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [dateFilter, setDateFilter] = useState('today');
  const [invCustomStart, setInvCustomStart] = useState('');
  const [invCustomEnd, setInvCustomEnd] = useState('');
  // Invoice-type filter (by item type, or a course's dose bill). Empty = show all. Multi-select so
  // the user can load medicine invoices, token/service invoices, or both.
  const [typeFilters, setTypeFilters] = useState([]); // subset of ['medicine','service','course']
  // The words last searched for. Typing in the box fetches nothing until the search is run.
  const [appliedSearch, setAppliedSearch] = useState('');

  // The server sends one light row per bill, only for the days chosen, by the day the bill was
  // completed: a bill raised yesterday and completed today belongs under Today. It used to send
  // every bill ever made, whole, and the dates were picked out here: 17 MB for 552 bills on prod,
  // about two seconds, on 27 Oct 2026. A search looks across every date and the date chips then
  // narrow its matches below. Only the last request's answer is kept, so a slow answer for the
  // previous chip cannot replace the one asked for since.
  const [listFrom, listTo] = invoiceListDays(dateFilter, invCustomStart, invCustomEnd);
  const rangeFrom = appliedSearch ? null : listFrom;
  const rangeTo = appliedSearch ? null : listTo;
  const request = useMemo(
    () => (appliedSearch ? { term: appliedSearch } : { from: rangeFrom, to: rangeTo }),
    [appliedSearch, rangeFrom, rangeTo],
  );
  const latestLoad = useRef(0);
  const loadInvoices = useCallback(async ({ term, from, to }) => {
    const n = ++latestLoad.current;
    setLoading(true);
    try {
      const data = term ? await invoiceAPI.search(term) : await invoiceAPI.getAll({ from, to });
      if (n === latestLoad.current) setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load invoices:', err);
      if (n === latestLoad.current) setInvoices([]);
    } finally {
      if (n === latestLoad.current) setLoading(false);
    }
  }, []);

  useEffect(() => { if (view === 'list') loadInvoices(request); }, [view, request, loadInvoices]);

  // Re-clicking the "Invoice" sidebar item resets us back to the counter (App also sets #/invoice,
  // which closes whatever bill the counter had open).
  useEffect(() => {
    const onReset = (e) => {
      if (e?.detail?.moduleId === 'invoice') {
        setView(firstTab());
        setSelectedInvoice(null);
      }
    };
    window.addEventListener('clinic:module-reset', onReset);
    return () => window.removeEventListener('clinic:module-reset', onReset);
  }, [firstTab]);

  const switchTab = (key) => {
    // Leaving the counter drops its open bill from the address, so a refresh on another tab does
    // not reopen it.
    if (key !== 'counter' && parseCounterHash()) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/invoice`);
    }
    setView(key);
  };

  const handleSearchInvoices = () => {
    setDateFilter('all');
    setAppliedSearch(searchQuery.trim());
  };

  const openInvoiceDetail = async (invoice) => {
    // A bill still in progress is finished where bills are made.
    if (invoice.status === 'DRAFT') {
      if (!can('VIEW_COUNTER_TAB')) {
        showToast('info', 'This bill is still in progress. It is finished at the counter.');
        return;
      }
      setView('counter');
      window.location.hash = counterHash('bill', invoice.id);
      return;
    }
    try {
      const full = await invoiceAPI.getById(invoice.id);
      setSelectedInvoice(full);
      setView('detail');
    } catch (err) {
      showToast('error', 'Failed to load invoice details');
    }
  };

  const goBackToList = () => {
    setView('list');
    setSelectedInvoice(null);
  };

  // The server already sent only the chosen days; this narrows a search's matches, which cover
  // every date, by the date the bill was completed. A bill that has not been
  // completed has no such date, so it sits outside every range and shows under All: unfinished
  // bills are picked up on the Counter tab, and the Not Completed Same Day report lists them.
  const [invRangeStart, invRangeEnd] = invoiceListRange(dateFilter, invCustomStart, invCustomEnd);
  const displayedInvoices = invoices.filter(inv => {
    // Date range
    if (invRangeStart || invRangeEnd) {
      // Backend timestamps are naive clinic wall-clock, so they must be parsed by toClinicDate
      // rather than new Date(), which reads them as UTC and shifts the day west of it.
      const d = inv.paidAt ? toClinicDate(inv.paidAt) : null;
      if (!d) return false;
      if (invRangeStart && d < invRangeStart) return false;
      if (invRangeEnd && d >= invRangeEnd) return false;
    }
    // Type (by item type) — invoice matches if it has any item of a selected type.
    if (typeFilters.length > 0) {
      const matches = typeFilters.some(t =>
        t === 'medicine' ? inv.hasMedicine
        : t === 'service' ? inv.hasService
        : t === 'course' ? isCourseDose(inv)
        : false);
      if (!matches) return false;
    }
    return true;
  });

  // Bills that share a prescription with another bill that is not cancelled. The server judges it
  // against every bill, not only the days loaded, and allows one bill per prescription (course
  // doses have their own), so this should stay empty; if not, those bills are marked to look at.
  const duplicates = useMemo(
    () => new Set(invoices.filter((inv) => inv.duplicate).map((inv) => inv.id)),
    [invoices],
  );

  const toggleTypeFilter = (t) =>
    setTypeFilters(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const heading = view === 'tokenReceipts' ? 'Token Receipts' : view === 'drawers' ? 'Cash Drawers' : 'Invoices';
  const subheading = view === 'tokenReceipts'
    ? 'Consultation fees the patient paid when the token was issued'
    : view === 'drawers'
      ? "Every cashier's drawer: the cash that went in and out, and the count at closing"
      : view === 'counter'
        ? 'Bill the patient, collect the payment and print the receipt, in one place'
        : 'Bills for medicines & services, raised after the consultation';

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <Receipt className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{heading}</h1>
                <p className="text-gray-600">{subheading}</p>
              </div>
            </div>
            {view === 'detail' && (
              <button type="button" onClick={goBackToList} className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                <ChevronRight className="h-4 w-4 rotate-180" /> Back to Invoices
              </button>
            )}
          </div>
        </div>

        {view !== 'detail' && (
          <div className="flex gap-2 mb-6 flex-wrap" role="group" aria-label="Invoice views">
            {[
              { key: 'counter', label: 'Counter', icon: Banknote, allowed: can('VIEW_COUNTER_TAB') },
              { key: 'list', label: 'All invoices', icon: Receipt, allowed: can('VIEW_ALL_INVOICES_TAB') },
              { key: 'tokenReceipts', label: 'Token Receipts', icon: CreditCard, allowed: can('VIEW_TOKEN_RECEIPTS_TAB') },
              { key: 'drawers', label: 'Cash Drawers', icon: Wallet, allowed: mayViewDrawers },
            ].filter(t => t.allowed).map(t => {
              const Icon = t.icon;
              const active = view === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => switchTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
        )}

        {view === 'counter' && can('VIEW_COUNTER_TAB') && <BillingCounter can={can} />}

        {view === 'tokenReceipts' && can('VIEW_TOKEN_RECEIPTS_TAB') && <OpdReceiptsView />}

        {view === 'none' && (
          <div className="bg-white rounded-2xl shadow-xl p-10 text-center">
            <p className="font-semibold text-gray-900">No part of Invoices is open to you yet.</p>
            <p className="mt-1 text-sm text-gray-600">
              Ask an administrator for the counter, the invoice list, token receipts or the cash drawers.
            </p>
          </div>
        )}

        {view === 'drawers' && mayViewDrawers && <CashDrawersView hasPermission={hasPermission} />}

        {view === 'list' && can('VIEW_ALL_INVOICES_TAB') && (
          <InvoiceListView
            invoices={displayedInvoices}
            loading={loading}
            searchQuery={searchQuery}
            setSearchQuery={(v) => {
              setSearchQuery(v);
              // An emptied box goes back to the days chosen.
              if (!v.trim()) setAppliedSearch('');
            }}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            customStart={invCustomStart}
            setCustomStart={setInvCustomStart}
            customEnd={invCustomEnd}
            setCustomEnd={setInvCustomEnd}
            typeFilters={typeFilters}
            toggleTypeFilter={toggleTypeFilter}
            onSearch={handleSearchInvoices}
            onRefresh={() => loadInvoices(request)}
            onOpenInvoice={openInvoiceDetail}
            duplicates={duplicates}
          />
        )}

        {view === 'detail' && selectedInvoice && (
          <InvoiceDetailView
            invoice={selectedInvoice}
            showToast={showToast}
            can={can}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// INVOICE LIST VIEW
// ============================================================================
const InvoiceListView = ({ invoices, loading, searchQuery, setSearchQuery, dateFilter, setDateFilter, customStart, setCustomStart, customEnd, setCustomEnd, typeFilters, toggleTypeFilter, onSearch, onRefresh, onOpenInvoice, duplicates = new Set() }) => {
  const todayIso = clinicToday();
  const TYPE_OPTIONS = [
    { value: 'medicine', label: 'Medicines' },
    { value: 'service', label: 'Additional Services' },
    { value: 'course', label: 'Injection doses' },
  ];
  // Unsorted to begin with, which leaves the server's newest-first order in place. What each
  // column sorts on is stated here rather than read off the rendered cell, so Total orders by
  // amount and not by the formatted "Rs 1,000" string.
  const { sortConfig, handleSort } = useSort();
  const sortedInvoices = useMemo(() => sortRows(invoices, sortConfig, {
    invoiceNumber: (i) => i.invoiceNumber,
    patient:       (i) => `${i.patient?.firstName || ''} ${i.patient?.lastName || ''}`.trim(),
    rx:            (i) => i.prescription?.prescriptionNumber,
    total:         (i) => Number(i.grossTotal || 0),
    discount:      (i) => Number(i.totalDiscount || 0),
    status:        (i) => (INVOICE_STATUS_CONFIG[i.status] || INVOICE_STATUS_CONFIG.DRAFT).label,
    // The stored ISO string, not a Date: it already sorts chronologically and never has to be
    // parsed, which is what shifts a date-only field a day west of UTC. A bill that was never
    // completed has no date at all, so it sorts as an empty string and the blanks group together.
    date:          (i) => i.paidAt || '',
  }), [invoices, sortConfig]);
  const paging = usePagedRows(sortedInvoices, {
    id: 'invoices',
    resetOn: [sortConfig, searchQuery, dateFilter, customStart, customEnd, typeFilters],
  });
  // Said under the count, so a day with more invoices than visits explains itself.
  const doses = invoices.filter(isCourseDose).length;
  const flagged = invoices.filter((inv) => duplicates.has(inv.id)).length;
  const countNote = doses > 0 || flagged > 0 ? (
    <div className="space-y-0.5 text-xs">
      {doses > 0 && doses < invoices.length && (
        <p className="text-gray-500">
          <Syringe className="inline h-3 w-3 text-sky-600 mr-1 -mt-0.5" aria-hidden="true" />
          {doses === 1 ? '1 of these is an injection dose' : `${doses} of these are injection doses`}: a patient back for the
          next dose of a course prescribed on an earlier visit. No token is taken for it, so visit counts leave {doses === 1 ? 'it' : 'them'} out.
        </p>
      )}
      {flagged > 0 && (
        <p className="font-medium text-rose-700">
          <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5" aria-hidden="true" />
          {flagged} bills share a prescription with another bill that is not cancelled. Open them and cancel the one that should not be there.
        </p>
      )}
    </div>
  ) : null;
  return (
    <>
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-xl p-5 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              placeholder="Search invoices by patient name, MRN, phone, or invoice#..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {INVOICE_DATE_FILTERS.map(f => (
              <button
                key={f.value}
                type="button"
                onClick={() => setDateFilter(f.value)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  dateFilter === f.value
                    ? 'bg-navy-800 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button type="button" title="Refresh" aria-label="Refresh" onClick={onRefresh} disabled={loading} className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50">
            <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {/* Invoice type filter — multi-select. None selected = all types. */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-1">Type</span>
          {TYPE_OPTIONS.map(t => {
            const on = typeFilters.includes(t.value);
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => toggleTypeFilter(t.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  on ? 'bg-navy-800 border-navy-800 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            );
          })}
          <span className="text-xs text-gray-400 ml-1">
            {typeFilters.length === 0 ? '(showing all types)' : ''}
          </span>
        </div>
        {/* Custom date range */}
        {dateFilter === 'custom' && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
              <div className="w-44">
                <DatePicker value={customStart} onChange={setCustomStart} max={customEnd || todayIso} placeholder="Start date" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
              <div className="w-44">
                <DatePicker value={customEnd} onChange={setCustomEnd} min={customStart || undefined} max={todayIso} placeholder="End date" />
              </div>
            </div>
            {(customStart || customEnd) && (
              <button
                type="button"
                onClick={() => { setCustomStart(''); setCustomEnd(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
              >
                Clear dates
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-xl">
        {!loading && <TablePager paging={paging} noun="invoice" toolbar={countNote} />}
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {/* Date leads: an invoice list is read by when, and the invoice number is a
                  reference you look up once you have found the day. */}
              <SortHeader label="Completed" sortKey="date" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-4" />
              <SortHeader label="Invoice#" sortKey="invoiceNumber" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-4" />
              <SortHeader label="Patient" sortKey="patient" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-4" />
              <SortHeader label="Rx#" sortKey="rx" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-4" />
              {/* Total is every line at its full price, the bill's "Total Amount", and Discount is
                  everything taken off it. A bill given away free reads Rs 196.35 less Rs 196.35,
                  not a bare Rs 0 that looks like nothing was sold. */}
              <SortHeader label="Total" sortKey="total" sortConfig={sortConfig} onSort={handleSort} align="right" className="px-6 py-4" />
              <SortHeader label="Discount" sortKey="discount" sortConfig={sortConfig} onSort={handleSort} align="right" className="px-6 py-4" />
              <SortHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-4" />
              {/* Actions holds a button, not a value, so there is nothing to order it by. */}
              <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}><td colSpan={8} className="px-6 py-4"><div className="h-10 bg-gray-200 rounded animate-pulse"></div></td></tr>
              ))
            ) : sortedInvoices.length > 0 ? (
              paging.pageRows.map((inv) => {
                const sc = INVOICE_STATUS_CONFIG[inv.status] || INVOICE_STATUS_CONFIG.DRAFT;
                const duplicate = duplicates.has(inv.id);
                return (
                  <tr key={inv.id} tabIndex={0} className={`${duplicate ? 'bg-rose-50/60 hover:bg-rose-50' : 'hover:bg-gray-50'} transition-colors cursor-pointer ${ROW_FOCUS_RING}`} onClick={() => onOpenInvoice(inv)} onKeyDown={onActivateKey(() => onOpenInvoice(inv))}>
                    {/* When the bill was completed, date over time. The day it was raised is still
                        kept in the database, but it is not what anyone reads this list for. A bill
                        not yet completed has no date to show. */}
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {inv.paidAt ? (
                        <>
                          <div>{formatClinicDate(inv.paidAt)}</div>
                          <div className="text-xs text-gray-400">{formatClinicTime(inv.paidAt, {}, '')}</div>
                        </>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4"><span className="font-mono text-sm text-emerald-700 font-semibold">{inv.invoiceNumber}</span></td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{inv.patient?.firstName} {inv.patient?.lastName}</div>
                      <div className="text-xs text-gray-500">{inv.patient?.mrn}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 font-mono">{inv.prescription?.prescriptionNumber || '—'}</div>
                      {isCourseDose(inv) && <CourseDoseChip invoice={inv} className="mt-1" />}
                      {duplicate && (
                        <span
                          title="Another bill on this prescription is not cancelled. The system allows only one, so one of them should be cancelled."
                          className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 cursor-help whitespace-nowrap"
                        >
                          <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Second bill for this Rx
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right"><span className="font-semibold text-gray-900 tabular-nums">{formatCurrency(inv.grossTotal)}</span></td>
                    <td className="px-6 py-4 text-right">
                      {Number(inv.totalDiscount) > 0 ? (
                        <span className="text-sm font-medium text-emerald-600 tabular-nums">- {formatCurrency(inv.totalDiscount)}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.color} ${inv.status === 'CANCELLED' && inv.cancellationReason ? 'cursor-help' : ''}`}
                        title={inv.status === 'CANCELLED' && inv.cancellationReason ? `Reason: ${inv.cancellationReason}` : undefined}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>{sc.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title={inv.status === 'DRAFT' ? 'Open on the counter' : 'View'}
                        aria-label={inv.status === 'DRAFT' ? `Open ${inv.invoiceNumber} on the counter` : `View ${inv.invoiceNumber}`}
                        onClick={(e) => { e.stopPropagation(); onOpenInvoice(inv); }}
                      >
                        <Eye className="h-4 w-4 text-gray-500" />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={8} className="px-6 py-16 text-center">
                <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-500 mb-1">No invoices found</h3>
                <p className="text-gray-400 text-sm">
                  {dateFilter === 'today' ? 'No invoices created today.'
                    : dateFilter === 'all' ? 'Try a different search.'
                    : 'No invoices in the selected date range.'}
                </p>
              </td></tr>
            )}
          </tbody>
        </table>
        {!loading && <TablePager paging={paging} noun="invoice" position="bottom" />}
      </div>
    </>
  );
};

// ============================================================================
// INVOICE DETAIL VIEW — a paid or cancelled bill, read-only
// ============================================================================
// Bills are made and paid on the counter. This view is the record of one afterwards: every line,
// the round off, how it was paid, the cash handed over and the change, and who took the money.
const InvoiceDetailView = ({ invoice, showToast, can }) => {
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);
  const [paper, setPaper] = useState('SLIP_80MM');
  const [branding, setBranding] = useState(null);
  const [printA4, setPrintA4] = useState(0);
  // The receipt dialog: a preview of the slip (or the A4 page), then Print or Close.
  const [showReceipt, setShowReceipt] = useState(false);

  useEffect(() => {
    let live = true;
    clinicalSettingsAPI.get()
      .then((s) => { if (live) setPaper(s?.billingReceiptPaper === 'A4' ? 'A4' : 'SLIP_80MM'); })
      .catch(() => {});
    brandingAPI.get().then((b) => { if (live) setBranding(b); }).catch(() => {});
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!printA4) return undefined;
    const t = setTimeout(() => window.print(), 50);
    return () => clearTimeout(t);
  }, [printA4]);

  const sc = INVOICE_STATUS_CONFIG[invoice.status] || INVOICE_STATUS_CONFIG.DRAFT;
  const isPaid = invoice.status === 'PAID';
  const items = [...(invoice.items || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const totals = invoiceTotals(invoice);
  const doctor = receiptDoctorName(invoice);

  return (
    <div className="space-y-6">
      {/* Invoice Header */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900 font-mono">{invoice.invoiceNumber}</h2>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${sc.color}`}>
                <span className={`w-2 h-2 rounded-full ${sc.dot}`}></span>{sc.label}
              </span>
            </div>
            <div className="text-sm text-gray-600 space-y-0.5">
              <p>Patient: <span className="font-semibold text-gray-900">{invoice.patient?.firstName} {invoice.patient?.lastName}</span> ({invoice.patient?.mrn})</p>
              <p>Rx#: <span className="font-mono">{invoice.prescription?.prescriptionNumber}</span>{doctor ? ` · ${doctor}` : ''}</p>
              {isCourseDose(invoice) && <p><CourseDoseChip invoice={invoice} /></p>}
              {/* Only the completed date is shown. The day the bill was raised stays in the
                  database, where the Not Completed Same Day report reads it. */}
              {invoice.paidAt && <p>Completed: {formatClinicDateTime(invoice.paidAt, { withZone: true })}{invoice.collectedByName ? ` · by ${invoice.collectedByName}` : ''}</p>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900 tabular-nums">{formatCurrency(invoice.grandTotal)}</div>
            {Number(invoice.amountPending) > 0 && <div className="text-sm text-red-600 font-semibold">Pending: {formatCurrency(invoice.amountPending)}</div>}
            {isPaid && allowed('PRINT_INVOICE') && (
              <button type="button" onClick={() => setShowReceipt(true)} className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                <Printer className="h-4 w-4" /> Print receipt
              </button>
            )}
          </div>
        </div>
        {invoice.status === 'CANCELLED' && invoice.cancellationReason && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-red-700">Cancellation reason: </span>
              <span className="text-red-700">{invoice.cancellationReason}</span>
              {invoice.cancelledAt && (
                <span className="text-red-500 ml-2 text-xs">
                  · {formatClinicDateTime(invoice.cancelledAt, { withZone: true })}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Invoice Items ({items.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Item</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Qty</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Unit Price</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Discount</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, idx) => {
                const TypeIcon = itemIcon(item);
                const typeLabel = item.itemType === 'MEDICINE' ? 'Medicine' : item.itemType === 'MEDICAL_SERVICE' ? 'Service' : 'Extra';
                const itemDiscount = Number(item.discountAmount || 0);
                return (
                  <tr key={item.id || idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-400">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm flex items-center gap-2 flex-wrap">
                            {item.name}
                            {/* Paying dispenses inventory medicines — surface that on the item */}
                            {isPaid && item.itemType === 'MEDICINE' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                                Dispensed
                              </span>
                            )}
                          </div>
                          {item.description && <div className="text-xs text-gray-500">{item.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        item.itemType === 'MEDICINE' ? 'bg-purple-100 text-purple-700' :
                        item.itemType === 'MEDICAL_SERVICE' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{typeLabel}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700 tabular-nums">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatCurrency(item.unitPrice)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm tabular-nums ${itemDiscount > 0 ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
                        {itemDiscount > 0 ? `- ${formatCurrency(itemDiscount)}` : '—'}
                        {/* The rate that produced it: the money is what matters, but "10%" is what was agreed. */}
                        {itemDiscount > 0 && Number(item.discountPercent) > 0 && (
                          <span className="text-xs text-gray-400 ml-1">({Number(item.discountPercent)}%)</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatCurrency(item.total)}</td>
                  </tr>
                );
              })}
            </tbody>
            {/* The bill's working, as the slip prints it: every line at full price, everything taken
                off (line discounts and any additional discount together), then what was charged. */}
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td colSpan={6} className="px-4 py-3 text-right text-sm font-medium text-gray-600">Total Amount</td>
                <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatRs(totals.gross / 100)}</td>
              </tr>
              {totals.discount > 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-2 text-right text-sm font-medium text-emerald-600">Discount</td>
                  <td className="px-4 py-2 text-right text-sm font-semibold text-emerald-600 tabular-nums">- {formatRs(totals.discount / 100)}</td>
                </tr>
              )}
              {totals.tax > 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-2 text-right text-sm font-medium text-gray-600">Tax</td>
                  <td className="px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{formatRs(totals.tax / 100)}</td>
                </tr>
              )}
              {totals.rounding !== 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-2 text-right text-sm font-medium text-gray-600">Round off</td>
                  <td className="px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{formatSigned(invoice.roundingAdjustment)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={6} className="px-4 py-3 text-right text-sm font-bold text-gray-900">Final Amount</td>
                <td className="px-4 py-3 text-right text-lg font-bold text-gray-900 tabular-nums">{formatCurrency(invoice.grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Payment */}
      {isPaid && (
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h3 className="font-bold text-gray-900 mb-3">Payment</h3>
          {toPaisa(invoice.grandTotal) === 0 ? (
            <p className="text-sm text-gray-600">No charge. The bill was fully discounted.</p>
          ) : (
            <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              <div className="flex justify-between gap-4"><dt className="text-gray-500">Paid by</dt><dd className="font-medium text-gray-900">{PAYMENT_MODE_LABEL[invoice.paymentMode] || invoice.paymentMode || 'Not recorded'}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-gray-500">Amount</dt><dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(invoice.amountPaid)}</dd></div>
              {invoice.paymentReference && (
                <div className="flex justify-between gap-4"><dt className="text-gray-500">Reference</dt><dd className="font-medium text-gray-900">{invoice.paymentReference}</dd></div>
              )}
              {invoice.paymentMode === 'CASH' && invoice.cashTendered != null && (
                <>
                  <div className="flex justify-between gap-4"><dt className="text-gray-500">Cash received</dt><dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(invoice.cashTendered)}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-gray-500">Change given</dt><dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(invoice.changeReturned)}</dd></div>
                </>
              )}
              {invoice.collectedByName && (
                <div className="flex justify-between gap-4"><dt className="text-gray-500">Received by</dt><dd className="font-medium text-gray-900">{invoice.collectedByName}</dd></div>
              )}
            </dl>
          )}
        </div>
      )}

      {!isPaid && invoice.status !== 'CANCELLED' && (
        <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center gap-3 text-sm text-gray-600">
          <FileText className="h-4 w-4 text-gray-400" />
          This invoice is not paid.
        </div>
      )}

      {showReceipt && (
        <InvoiceReceiptModal
          invoice={invoice}
          branding={branding}
          paper={paper}
          isReprint
          onClose={() => setShowReceipt(false)}
          onPrintA4={() => setPrintA4((n) => n + 1)}
          onPrintBlocked={() => showToast('error', 'Could not open the print window. Please allow pop-ups for this site.')}
        />
      )}

      {/* Printable Invoice — hidden on screen, printed when the receipt paper setting is A4 */}
      <PrintableInvoice invoice={invoice} branding={branding} />
    </div>
  );
};

export default InvoiceManagement;
